import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { uiLocale } from '@/lib/i18n';
import { possessiveName } from '@/utils/possessiveName';

export function RevealCprModal({
  open,
  patientFirstName,
  onClose,
  onReveal,
  loading,
}: {
  open: boolean;
  patientFirstName: string;
  onClose: () => void;
  onReveal: (reason: string) => Promise<void>;
  loading: boolean;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!open) {
      setReason('');
      setSubmitted(false);
    }
  }, [open]);

  const trimmed = reason.trim();
  const error = submitted && !trimmed ? t('patients.cpr_modal.reason_required') : undefined;
  const name = possessiveName(patientFirstName, uiLocale());

  const handleReveal = async () => {
    setSubmitted(true);
    if (!trimmed) return;
    await onReveal(trimmed);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('patients.cpr_modal.title', { name })}
      description={t('patients.cpr_modal.body', { name })}
      footer={
        <>
          <Button type="button" variant="tertiary" onClick={onClose} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            onClick={() => void handleReveal()}
            loading={loading}
            loadingText={t('patients.cpr_reveal')}
          >
            {t('patients.cpr_reveal')}
          </Button>
        </>
      }
    >
      <Input
        label={t('patients.cpr_modal.reason')}
        name="cpr-reveal-reason"
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        required
        error={error}
        autoFocus
      />
    </Modal>
  );
}
